<template>
  <div v-on:touchmove="iOSpreventGlobalTouchMove" v-on:touchend="iOSpreventGlobalTouchMoveEnd">
    <div v-if="loadingStatus == 'loading'" class="overlay">
      <img src="/images/loader.gif" alt="Processing…" style="width: 25px; height: 25px; display: inline-block; margin: 10px" />
      {{ I18n.t("loading_image") }}
    </div>
    <div v-if="loadingStatus == 'error'" class="overlay">
      <div class="alert alert-danger">
        <p><strong>{{ I18n.t("failed_to_open_image") }}</strong></p>

        <p>
          <small>{{ this.loadingErrorMessage }}</small>
        </p>

        <p>
          {{ I18n.t("please_try_again_or_contact_us") }}
        </p>
      </div>
    </div>
    <download-overlay
      v-bind:url="downloadUrlResult ? downloadUrlResult.url : null"
      v-bind:blob="downloadUrlResult ? downloadUrlResult.blob : null"
      v-bind:filename="filename"
      v-bind:locale="locale" v-if="downloading"
      v-on:close="downloading = false; expireDownload();"
      v-on:downloaded="warnBeforeClose = false;"
    />
    <div v-bind:class="{'editor-layout': true, 'editor-layout-has-control-area-opened': controlTabsOpened}">
      <div class="editor-area">
        <div class="editor-upper-controls">
          <button :title="I18n.t('modal_close')" class="editor-button close-button" v-on:click="close">
            <i class="fas fa-times"></i>
          </button>
          <div class="editor-button-group d-none d-sm-block">
            <button :title="I18n.t('zoom_out')" class="editor-button zoom-out-button" v-on:click="zoomOut" v-bind:disabled="!canZoomOut">
              <i class="fal fa-minus"></i>
            </button>
            <button :title="I18n.t('set_zoom_to_100_percent')" class="editor-button zoom-restore-button" v-on:click="zoomReset">
              {{ Math.round(zoom * 100) }}%
            </button>
            <button :title="I18n.t('zoom_in')" class="editor-button zoom-in-button" v-on:click="zoomIn" v-bind:disabled="!canZoomIn">
              <i class="fal fa-plus"></i>
            </button>
          </div>
          <div class="editor-button-group">
            <button :title="I18n.t('undo')" class="editor-button undo-button" v-on:click="undo" v-bind:disabled="!canUndo">
              <i class="fal fa-undo-alt"></i>
            </button>
            <button :title="I18n.t('redo')" class="editor-button redo-button" v-on:click="redo" v-bind:disabled="!canRedo">
              <i class="fal fa-redo-alt"></i>
            </button>
          </div>
          <button :title="I18n.t('download')" class="editor-button download-button d-sm-none" v-on:click="download">
            <i class="fas fa-download"></i>
          </button>
        </div>
        <div v-bind:class="{'editor-area-content': true, 'tool-active': tool != null }" v-on:mousemove="updateCursorPosition" v-on:touchstart.passive="touchStarted" v-on:touchmove="onTouchMove" ref="editorArea">
          <div class="editor-area-content-inner" ref="editorCanvas" v-on:contextmenu="$event.preventDefault()" v-bind:data-ios-emulate-scroll="tool == 'erase_restore' ? false : 'xy'" data-hj-suppress></div>
          <div v-bind:class="{'editor-cursor': true, 'touchdown': isTouchDown}" v-bind:style="{
            left: `${cursorPosition[0]}px`,
            top: `${cursorPosition[1] - brushOffset}px`,
            'margin-left': `-${this.zoom * brushSize / 2}px`,
            'margin-top': `-${this.zoom * brushSize / 2}px`,
            width: `${this.zoom * brushSize + 2}px`,
            height: `${this.zoom * brushSize + 2}px`,
          }">
            <div class="editor-finger-cursor" v-if="touchDetected" v-bind:style="{
              top: `${this.zoom * brushSize/2 + brushOffset}px`,
            }">
              <i class="fal fa-hand-pointer"></i>
            </div>
          </div>
        </div>
        <div class="editor-lower-controls d-none d-sm-block">
          <button :title="I18n.t('download')" class="btn btn-primary" v-on:click="download">
            <i class="fas fa-download"></i> {{ I18n.t("download") }}
          </button>
        </div>
      </div>
      <div class="control-area">
        <div class="control-tab-buttons">
          <button v-bind:class="{'tab-button': true, 'tab-button-active': (controlTab == 'background' && controlTabsOpened) }" v-on:click="showTab('background')">
            <i v-bind:class="{'fas': (controlTab == 'background' && controlTabsOpened), 'fal': !(controlTab == 'background' && controlTabsOpened), 'fa-layer-group': true}"></i>
            {{ I18n.t("tab_background") }}
          </button>
          <button v-bind:class="{'tab-button': true, 'tab-button-active': (controlTab == 'erase_restore' && controlTabsOpened) }" v-on:click="showTab('erase_restore')" v-on:touchstart="touchDetected = true">
            <i v-bind:class="{'fas': (controlTab == 'erase_restore' && controlTabsOpened), 'fal': !(controlTab == 'erase_restore' && controlTabsOpened), 'fa-paint-brush': true}"></i>
            {{ I18n.t("tab_erase_restore") }}
          </button>
          <button v-bind:class="{'tab-button': true, 'd-sm-none': true }" v-on:click="showTab(null);" v-if="controlTabsOpened">
            <i class="fal fa-chevron-down"></i>
            {{ I18n.t("tab_close") }}
          </button>
          <button v-bind:class="{'tab-button': true, 'd-sm-none': true }" v-on:click="showTab(controlTab);" v-if="!controlTabsOpened">
            <i class="fal fa-chevron-up"></i>
            {{ I18n.t("tab_open") }}
          </button>
        </div>
        <div class="control-tabs" data-ios-emulate-scroll="y">
          <div class="control-tab" v-if="controlTab == 'background'">
            <div class="background-tab-content">
              <div style="font-size: 70%;">
                {{ I18n.t("blur") }}
              </div>
              <blur-options
                v-bind:value="background.blur ? background.blur : 0"
                v-bind:foregroundUrl="foregroundThumbnail"
                v-bind:backgroundUrl="background.type == 'image' ? background.image : originalUrl"
                v-on:change="change({background: {type: 'image', image: (background.type == 'image' ? background.image : originalUrl), blur: $event.newValue}});" />

              <div style="margin: 10px auto; text-align: center;" class="background-type-toggle">
                <toggle v-bind:initial-value="backgroundTypeTab" left-value="photo" :left-label="I18n.t('photo')" right-value="color" :right-label="I18n.t('color')" v-on:change="backgroundTypeTab = $event.newValue;" />
              </div>

              <div v-bind:class="{'background-type-tab': true, 'background-type-tab-active': backgroundTypeTab == 'photo'}">
                <div style="font-size: 70%;">
                  {{ I18n.t("photo") }}
                </div>
                <image-list
                  v-bind:foregroundType="foregroundType"
                  v-bind:value="background.type == 'image' ? background.image : null"
                  v-bind:foregroundUrl="foregroundThumbnail"
                  v-bind:locale="locale"
                  v-on:change="change({background: {type: 'image', image: $event.newValue, blur: background.blur}});"
                  v-on:openSearchBackgroundOverlay="openSearchBackgroundOverlay"
                  v-on:setPixabayImages="setPixabayImages"
                  />
              </div>

              <div v-bind:class="{'background-type-tab': true, 'background-type-tab-active': backgroundTypeTab == 'color'}">
                <div style="font-size: 70%;">
                  {{ I18n.t("color") }}
                </div>
                <color-list
                  v-bind:value="background.type == 'color' ? background.color : null"
                  v-bind:foregroundUrl="foregroundThumbnail"
                  v-bind:transparent="background.type == 'transparent' ? true : false"
                  v-on:change="change($event.newValue ? {background: {type: 'color', color: $event.newValue}} : {background: {type: 'transparent'}});" />
                </div>
            </div>

          </div>
          <div class="control-tab" v-if="controlTab == 'erase_restore'">
            <div style="margin: 10px auto; text-align: center;">
              <toggle v-bind:initial-value="brushMode" left-value="erase" :left-label="I18n.t('erase')" right-value="restore" :right-label="I18n.t('restore')" v-on:change="editor.setMode($event.newValue); brushMode = $event.newValue;" />
            </div>

            <div style="max-width: 280px; margin: 10px auto;">
              <div style="font-size: 70%;">
                {{ I18n.t("brush_size") }}
              </div>
              <slider
                v-bind:initial-value="brushSize"
                v-bind:min-value="4"
                v-bind:max-value="brushSizeMax"
                v-on:change="editor.setBrushSize($event.newValue); brushSize = $event.newValue;"
                v-on:drag="isTouchDown = true"
                v-on:drop="isTouchDown = false"
                />
              <div v-if="touchDetected">
                <div style="font-size: 70%;">
                  {{ I18n.t("offset") }}
                </div>
                <offset-slider
                  v-bind:initial-value="brushOffset"
                  v-bind:min-value="-brushSizeMax"
                  v-bind:max-value="brushSizeMax"
                  v-on:change="editor.setBrushOffset($event.newValue); brushOffset = $event.newValue;"
                  v-on:drag="isTouchDown = true"
                  v-on:drop="isTouchDown = false"
                  />
              </div>
            </div>
          </div>
        </div>
      </div>
      <search-background-overlay
        v-bind:foregroundUrl="foregroundUrl"
        v-on:change="change({background: {type: 'image', image: $event.newValue, blur: background.blur}});"
        v-on:close="searchBackgroundOverlayOpen = false"
        v-bind:pixabayImages="pixabayImages"
        v-if="searchBackgroundOverlayOpen"
      />
    </div>
  </div>
</template>

<script>
import EditorUI from '../src/editor/editor_ui';
import Editor from '../src/editor/editor';
import Toggle from './toggle';
import Slider from './slider';
import OffsetSlider from './offset_slider';
import ColorList from './color_list';
import ImageList from './image_list';
import BlurOptions from './blur_options';
import DownloadOverlay from './download_overlay';
import SearchBackgroundOverlay from './search_background_overlay';

import isiOSSafari from '../src/is_ios_safari';

export default {
  props: {
    originalUrl: String,
    foregroundUrl: String,
    filename: String,
    foregroundType: String,
    locale: String,
  },
  data() {
    return {
      controlTabsOpened: true,
      controlTab: "background",
      loadingStatus: "loading",
      loadingErrorMessage: null,
      backgroundTypeTab: 'photo',
      editor: null,
      brushMode: 'erase',
      brushSize: 32,
      brushSizeMax: 100,
      brushOffset: 0,
      background: {
        type: 'transparent'
      },
      history: [],
      historyIndex: -1,
      foregroundThumbnail: null,
      foregroundThumbnailNeedsUpdate: false,
      zoom: 1.0,
      zoomMin: 0.2,
      zoomMax: 4.0,
      cursorPosition: [0, 0],
      isTouchDown: false,
      touchDetected: false,
      pinchPositions: null,
      downloading: false,
      downloadUrlResult: null,
      iosScrollingX: null,
      iosScrollingY: null,
      iosScrollingXPrevious: null,
      iosScrollingYPrevious: null,
      searchBackgroundOverlayOpen: false,
      pixabayImages: null,
      warnBeforeClose: false,
    };
  },
  mounted: function() {
    var vm = this;
    vm.editor = new Editor({
      target: vm.$refs.editorCanvas,
      original_url: vm.originalUrl,
      foreground_url: vm.foregroundUrl,
    });
    vm.editor.render().then(() => {
      vm.loadingStatus = "loaded";
      vm.foregroundThumbnail = vm.foregroundUrl;
      vm.$emit("loaded", {
        width: vm.editor.width + 500,
        height: vm.editor.height + 130
      });
      var maxDimension = (vm.editor.width > vm.editor.height ? vm.editor.width : vm.editor.height);
      vm.brushSize = maxDimension * 0.05;
      vm.editor.setBrushSize(vm.brushSize);
      var brushSizeMax = maxDimension * 0.1;
      if(brushSizeMax > vm.brushSizeMax) vm.brushSizeMax = brushSizeMax;

      var rect = this.$refs.editorArea.getBoundingClientRect();
      vm.cursorPosition = [
        rect.left + (rect.right - rect.left)/2,
        rect.top + (rect.bottom - rect.top)/2
      ];

      window.track("Images", "open_editor", "Open Editor");
    }).catch((e) => {
      vm.loadingStatus = "error";
      vm.loadingErrorMessage = e;
      console.error(e);
      if(window.error_tracking) window.error_tracking.sendError(e);
    });
    vm.editor.on("pathStopped", () => {
      vm.change('editor');
      vm.foregroundChanged();
      if(vm.editor.mode == "erase") {
        window.track("Editor", "pixels_erased", "Pixels erased");
      }
      else {
        window.track("Editor", "pixels_restored", "Pixels restored");
      }
    });
  },
  computed: {
    canUndo: function() {
      return this.historyIndex >= 0;
    },
    canRedo: function() {
      return this.historyIndex < this.history.length-1;
    },
    canZoomIn() {
      return this.zoom < this.zoomMax - 0.01;
    },
    canZoomOut() {
      return this.zoom > this.zoomMin + 0.01;
    },
    tool() {
      if(this.controlTabsOpened && this.controlTab == "erase_restore") {
        return "erase_restore";
      }
      return null;
    }
  },
  watch: {
    background: function(newValue, oldValue) {
      if(newValue.type == 'color') {
        this.editor.setBackground({color: newValue.color});
      }
      else if(newValue.type == 'image') {
        if(newValue.type != oldValue.type || newValue.image != oldValue.image) {
          this.editor.setBackground({image_url: newValue.image});
        }
      }
      else if(newValue.type == 'transparent') {
        this.editor.setBackground(false);
      }
      if(newValue.blur != oldValue.blur) {
        if(newValue.blur) {
          this.editor.setBackgroundBlur(newValue.blur);
        }
        else {
          this.editor.setBackgroundBlur(0);
        }
      }
    },
    zoom: function(newValue, oldValue) {
      var canvas = this.$refs.editorCanvas;
      var posX = (canvas.scrollLeft + canvas.clientWidth/2) / canvas.scrollWidth;
      var posY = (canvas.scrollTop + canvas.clientHeight/2) / canvas.scrollHeight;

      this.editor.setZoom(newValue);

      canvas.scrollLeft = canvas.scrollWidth * posX - canvas.clientWidth/2;
      canvas.scrollTop = canvas.scrollHeight * posY - canvas.clientHeight/2;
    },
    controlTab: function(newValue, oldValue) {
      if(newValue == 'background' && this.foregroundThumbnailNeedsUpdate) {
        this.updateForegroundThumbnail();
      }
    }
  },
  methods: {
    showTab: function(tab) {
      if(tab == null) {
        this.controlTabsOpened = false;
        this.editor.setMode(null);
      }
      else {
        this.controlTab = tab;
        this.controlTabsOpened = true;
        if(tab == 'erase_restore') {
          this.editor.setMode(this.brushMode);
        }
        else {
          this.editor.setMode(null);
        }
      }
    },
    change(diff) {
      this.warnBeforeClose = true;
      if(this.canRedo) {
        this.history.splice(this.historyIndex+1, this.history.length);
        this.editor.clearRedoStack();
      }
      var undo, redo;
      if(diff == 'editor') {
        undo = {editor: 'undo'};
        redo = {editor: 'redo'};
      }
      else {
        undo = {};
        for(var key in diff) {
          undo[key] = this[key];
        }
        redo = diff;
      }
      this.history.push({undo, redo});
      this.historyIndex++;
      if(diff != 'editor') {
        this.applyChange(diff);
      }
    },
    applyChange(diff) {
      if('editor' in diff) {
        this.editor[diff.editor]();
        this.foregroundChanged();
      }
      else {
        for(var key in diff) {
          this[key] = diff[key];
        }
      }
    },
    undo() {
      if(!this.canUndo) return;
      this.applyChange(this.history[this.historyIndex].undo);
      this.historyIndex--;
      window.track("Editor", "undo", "Undo");
    },
    redo() {
      if(!this.canRedo) return;
      this.applyChange(this.history[this.historyIndex+1].redo);
      this.historyIndex++;
      window.track("Editor", "redo", "Redo");
    },
    close() {
      if (!this.warnBeforeClose || window.confirm(I18n.t("warn_before_closing"))) {
        EditorUI.hide();
      }
    },
    foregroundChanged() {
      if(this.controlTab == 'background') {
        this.updateForegroundThumbnail();
      }
      else {
        this.foregroundThumbnailNeedsUpdate = true;
      }
    },
    updateForegroundThumbnail() {
      this.foregroundThumbnailNeedsUpdate = false;
      this.editor.toUrl({hideBackground: true, thumbnail: true}).then((urlResult) => {
        this.foregroundThumbnail = urlResult.url;
        // TODO: call urlResult.destroy() at some point
      });
    },
    download() {
      this.expireDownload();
      this.downloading = true;
      this.editor.toUrl().then((urlResult) => {
        this.downloadUrlResult = urlResult;
      });
    },
    expireDownload() {
      if(this.downloadUrlResult != null) {
        this.downloadUrlResult.destroy();
        this.downloadUrlResult = null;
      }
    },
    setZoom(newValue) {
      if(newValue > this.zoomMax) {
        this.zoom = this.zoomMax;
      }
      else if(newValue < this.zoomMin) {
        this.zoom = this.zoomMin;
      }
      else {
        this.zoom = newValue;
      }
    },
    zoomIn() {
      this.setZoom(this.zoom + 0.2);
      window.track("Editor", "zoom_in", "Zoom in");
    },
    zoomOut() {
      this.setZoom(this.zoom - 0.2);
      window.track("Editor", "zoom_out", "Zoom out");
    },
    zoomReset() {
      this.setZoom(1.0);
      window.track("Editor", "zoom_reset", "Zoom reset");
    },
    zoomFit() {
      var canvas = this.$refs.editorCanvas;
      var innerHeight = canvas.scrollHeight;
      var outerHeight = canvas.clientHeight;
      var innerWidth = canvas.scrollWidth;
      var outerWidth = canvas.clientWidth;
      var zoomHeight = outerHeight / innerHeight;
      var zoomWidth = outerWidth / innerWidth;
      var zoom = zoomWidth < zoomHeight ? zoomWidth : zoomHeight;
      if(zoom > 1.0) zoom = 1.0;
      this.setZoom(zoom);
    },
    updateCursorPosition(e) {
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches ? e.touches[0].clientY : e.clientY;

      var rect = this.$refs.editorArea.getBoundingClientRect();
      this.cursorPosition = [clientX - rect.left, clientY - rect.top];
    },
    touchStarted(e) {
      if(this.tool == "erase_restore") {
        this.isTouchDown = true;
        this.updateCursorPosition(e);
      }
      window.addEventListener('touchend', this.touchEnded);
    },
    onTouchMove(e) {
      if(e.touches.length == 2) {
        e.preventDefault();
        if(this.tool == "erase_restore") {
          this.editor.cancelPath();
        }
        var pos = [
          [e.touches[0].clientX, e.touches[0].clientY],
          [e.touches[1].clientX, e.touches[1].clientY],
        ];
        if(this.pinchPositions) {
          var diffx1 = this.pinchPositions[0][0] - this.pinchPositions[1][0];
          var diffy1 = this.pinchPositions[0][1] - this.pinchPositions[1][1];
          var dist1 = Math.sqrt(Math.pow(diffx1, 2) + Math.pow(diffy1, 2));

          var diffx2 = pos[0][0] - pos[1][0];
          var diffy2 = pos[0][1] - pos[1][1];
          var dist2 = Math.sqrt(Math.pow(diffx2, 2) + Math.pow(diffy2, 2));

          this.setZoom(this.zoom + this.zoom * (dist2-dist1)/document.documentElement.clientWidth);
        }
        this.pinchPositions = pos;
      }
      else if(e.touches.length == 1) {
        if(this.tool == "erase_restore") {
          e.preventDefault();
          this.updateCursorPosition(e);
        }
      }
    },
    touchEnded() {
      this.isTouchDown = false;
      this.pinchPositions = null;
      window.removeEventListener('touchend', this.touchEnded);
    },
    iOSpreventGlobalTouchMove(e) {
      if(isiOSSafari) {
        var el = e.target;
        while(el && el !== this.$el) {
          var val = el.getAttribute('data-ios-emulate-scroll');
          if(val === 'x') {
            this.iosScrollingX = el;
          }
          else if(val === 'y') {
            this.iosScrollingY = el;
          }
          else if(val === 'xy') {
            this.iosScrollingX = el;
            this.iosScrollingY = el;
          }
          if(this.iosScrollingX != null && this.iosScrollingY != null) break;
          el = el.parentNode;
        }

        if(this.iosScrollingX) {
          var x = e.touches[0].pageX;
          if(this.iosScrollingXPrevious != null) {
            var diffX = x - this.iosScrollingXPrevious;
            this.iosScrollingX.scrollLeft -= diffX;
          }
          this.iosScrollingXPrevious = x;
        }
        if(this.iosScrollingY) {
          var y = e.touches[0].pageY;
          if(this.iosScrollingYPrevious != null) {
            var diffY = y - this.iosScrollingYPrevious;
            this.iosScrollingY.scrollTop -= diffY;
          }
          this.iosScrollingYPrevious = y;
        }

        e.preventDefault();
        return false;
      }
    },
    iOSpreventGlobalTouchMoveEnd() {
      if(isiOSSafari) {
        if(this.iosScrollingX) {
          this.iosScrollingXPrevious = null;
          this.iosScrollingX = null;
        }
        if(this.iosScrollingY) {
          this.iosScrollingYPrevious = null;
          this.iosScrollingY = null;
        }
      }
    },
    setPixabayImages(pixabayImages) {
      this.pixabayImages = pixabayImages;
    },
    openSearchBackgroundOverlay(pixabayImages) {
      this.pixabayImages = pixabayImages;
      this.searchBackgroundOverlayOpen = true;
    }
  },
  destroyed: function() {
    // remove event listener
    this.touchEnded();
  },
  components: { Toggle, Slider, OffsetSlider, BlurOptions, ImageList, ColorList, DownloadOverlay, SearchBackgroundOverlay }
}
</script>

<style scoped>
.editor-layout {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
}
.editor-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 35px;
}
.editor-layout-has-control-area-opened .editor-area {
  bottom: 250px;
}
@media(min-width: 768px) {
  .editor-area {
    right: 400px;
    bottom: 0;
  }
  .editor-layout-has-control-area-opened .editor-area {
    bottom: 0;
  }
}
.editor-area-content {
  position: absolute;
  top: 40px;
  bottom: 0;
  left: 0;
  right: 0;
  display:-webkit-flex;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.editor-area-content.tool-active {
  cursor: none;
}
@media(min-width: 768px) {
  .editor-area-content {
    bottom: 60px;
    padding: 15px 15px 5px 15px;
  }
}
.editor-area-content-inner {
  overflow: auto;
  border: 1px solid #cbcbcb;
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCA3LjkzNyA3LjkzOCcgaGVpZ2h0PSczMCcgd2lkdGg9JzMwJz48ZyBmaWxsPScjZWVlZmYwJz48cGF0aCBwYWludC1vcmRlcj0nc3Ryb2tlIGZpbGwgbWFya2VycycgZD0nTS4wMTQuMDE0SDMuOTdWMy45N0guMDE0ek0zLjk3IDMuOTY4aDMuOTU0djMuOTU1SDMuOTd6Jy8+PC9nPjwvc3ZnPgo=");
  background-size: 25px;
  max-width: 100%;
  max-height: 100%;
  font-size: 0;
}
.editor-cursor {
  position: absolute;
  pointer-events: none;
  border: 1px solid rgba(0,0,0, 0.5);
  border-radius: 50%;
  opacity: 0.0;
  transition: opacity 700ms;
}
.editor-area-content.tool-active:hover .editor-cursor,
.editor-cursor.touchdown {
  opacity: 1.0;
  transition: opacity 0s;
}
.editor-finger-cursor {
  color: #fc1b44;
  position: absolute;
  left: 50%;
  font-size: 30px;
  width: 30px;
  height: 30px;
  margin-left: -15px;
  margin-top: -15px;
  line-height: 1;
}
.editor-upper-controls {
  display:-webkit-flex;
  display: flex;
  justify-content: space-between;
}
@media(min-width: 768px) {
  .editor-upper-controls {
    padding: 10px 7px;
  }
}
.editor-button-group {
  display:-webkit-flex;
  display: flex;
  justify-content: space-between;
  margin: 0 5px;
}
.editor-button {
  background: transparent;
  border: 0;
  line-height: 1;
  color: #585858;
  display: inline-block;
  vertical-align: middle;
  margin: 0 3px;
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0;
}
.editor-button[disabled] {
  opacity: 0.5;
}
.close-button {
  z-index: 18000;
}
.close-button, .download-button {
  width: 40px;
  height: 40px;
  font-size: 20px;
}
.undo-button, .redo-button {
  width: 40px;
  height: 40px;
  font-size: 20px;
}
.zoom-in-button, .zoom-out-button {
  width: 35px;
  height: 35px;
  font-size: 18px;
}
.zoom-restore-button {
  width: 50px;
  height: 30px;
  font-size: 13px;
  font-weight: 400;
  padding-top: 4px;
}
@media(min-width: 768px) {
  .close-button, .download-button {
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
  .close-button {
    color: #fff;
    background: #585858;
  }
  .undo-button, .redo-button, .zoom-in-button, .zoom-out-button {
    width: 30px;
    height: 30px;
  }
  .undo-button, .redo-button, .zoom-in-button, .zoom-out-button, .zoom-restore-button {
    -webkit-box-shadow: 0px 0px 3px 0px rgba(0,0,0,0.25);
    -moz-box-shadow: 0px 0px 3px 0px rgba(0,0,0,0.25);
    box-shadow: 0px 0px 3px 0px rgba(0,0,0,0.25);
  }
}
.editor-lower-controls {
  text-align: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  height: 60px;
}
.editor-lower-controls .btn {
  margin-bottom: 0;
}
.control-area {
  position: absolute;
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  height: 35px;
  display:-webkit-flex;
  display: flex;
  flex-direction: column;
}
.editor-layout-has-control-area-opened .control-area {
  height: 250px;
}
@media(min-width: 768px) {
  .control-area {
    top: 0;
    bottom: 0;
    left: auto;
    right: 0;
    width: 400px;
    height: auto;
  }
  .editor-layout-has-control-area-opened .control-area {
    height: auto;
  }
}
.control-tab-buttons {
  display:-webkit-flex;
  display: flex;
  align-items: stretch;
  min-height: 35px;
}
@media(min-width: 768px) {
  .control-tab-buttons {
    min-height: 45px;
  }
}
.control-tabs {
  flex: 1 1 auto;
  display:-webkit-flex;
  display: flex;
  max-height: 100%;
  overflow: auto;
  background: #2b2b2b;
  color: #fff;
}
.control-tab {
  flex: 1 1 auto;
  width: 100%;
  padding: 10px;
}
@media(min-width: 768px) {
  .control-tabs {
    background: #f4f4f4;
    color: #585858;
  }
}
.tab-button {
  flex: 1 1 auto;
  border: 0;
  color: #585858;
  background: #f4f4f4;
  font-size: 80%;
  font-weight: 400;
  line-height: 1;
  padding: 0 5px;
  border-radius: 0;
  outline: none;
  text-transform: uppercase;
}
.tab-button-active {
  color: #fff;
  background: #2b2b2b;
}
@media(min-width: 768px) {
  .tab-button {
    color: #585858;
    background: #fff;
    font-size: 100%;
    font-weight: 700;
    min-height: 45px;
    line-height: 1.3;
    padding-top: 3px;
  }
  .tab-button i {
    font-size: 110%;
    display: inline-block;
    margin-right: 5px;
  }
  .tab-button-active {
    color: #585858;
    background: #f4f4f4;
  }
}
.overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 15500;
  background: rgba(255, 255, 255, 0.8);
  display:-webkit-flex;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media(min-width: 768px) {
  .background-tab-content {
    margin-left: 15px;
  }
}
.background-type-toggle {
  display: none;
}
@media(min-width: 768px) {
  .background-type-toggle {
    display: block
  }
  .background-type-tab {
    display: none;
  }
  .background-type-tab-active {
    display: block;
  }
}
</style>
